import { Slider } from '../../../Slider';
import { getCompanyFinancialData } from '@/pages/Narrative/utils/calculation';
import { shouldConvertCurrency } from '@/pages/Narrative/utils/currencyConversion';

import type { NarrativeValuatorCompany } from '../../../../hooks/narrativeValuatorCompanyFragment';
import { tooltipContent } from '../../tooltipContent';
import { INPUT_VALUE_RANGES } from '../../../../utils/validation';
import { LabelledValue } from '../LabelledValue';
import { VALUATOR_RATE_PRECISION } from '@/pages/Narrative/utils/schema';
import { NarrativeValuatorTooltip } from '../NarrativeValuatorTooltip';
import { OperatorSymbol } from './components/OperatorSymbol';
import type { NarrativeValuatorForm } from '../../utils';
import type { CalculationBreakDownOutput } from '../../../Calculators';
import { toViewFormat } from '../../../Calculators/CalculationBreakDownTransformer';

export type Props = {
  company: NarrativeValuatorCompany;
  onChange: (data: NarrativeValuatorForm) => void;
  values: NarrativeValuatorForm;
  calculationBreakDownOutput: CalculationBreakDownOutput;
  mostRecentReportedYear: string;
};

export const CalculationBreakdown = ({
  company,
  onChange,
  values,
  mostRecentReportedYear,
  calculationBreakDownOutput,
}: Props) => {
  const { discountRatePercentage, priceEarningsMultiple } = values;
  const companyFinancialData = getCompanyFinancialData(company);
  const { listingCurrencyISO, reportingCurrencyISO } = companyFinancialData;

  const {
    currentYear,
    exchangeRate,
    fairValueListedPrice,
    fairValueReportedPrice,
    futureEarnings,
    futureMarketCap,
    futureSharesOutstanding,
    futureYear,
    sharePriceFutureYear,
  } = toViewFormat({
    calculationBreakDownOutput,
    reportingCurrencyISO: reportingCurrencyISO as unknown as string,
    listingCurrencyISO: listingCurrencyISO as unknown as string,
    currentYear: Number(mostRecentReportedYear ?? 0),
  });

  const requiresCurrencyConversion = shouldConvertCurrency({
    ...companyFinancialData,
    ...values,
  });

  const handleOnChange = (type: string) => (value: number) => {
    onChange({
      ...values,
      [type]: value,
    });
  };

  return (
    <div className="grid gap-x2">
      {/* Risk level section */}
      <div className="grid gap-x1">
        <div className="p-x2">
          <div className="grid grid-cols-[auto_min-content] items-center">
            <div
              id="discount-rate-percentage"
              className="text-xs font-strong text-solid"
            >
              Risk Level (Discount Rate)
            </div>
            <NarrativeValuatorTooltip>
              {tooltipContent.discountRate}
            </NarrativeValuatorTooltip>
          </div>
          <div className="text-xs text-soft">
            Our default considers factors like the company&apos;s size,
            volatility, profitablity and country of operation.
          </div>
        </div>
        <div className="p-x2">
          <div className="mb-x4">
            <Slider
              label="Risk Level (Discount Rate)"
              minRange={INPUT_VALUE_RANGES.discountRatePercentage.min}
              maxRange={INPUT_VALUE_RANGES.discountRatePercentage.max}
              value={discountRatePercentage}
              precision={VALUATOR_RATE_PRECISION}
              onChange={handleOnChange('discountRatePercentage')}
              suffix="%"
              bands={[
                {
                  threshold: INPUT_VALUE_RANGES.discountRatePercentage.min,
                  label: 'Decrease',
                  align: 'left',
                },
                {
                  threshold: INPUT_VALUE_RANGES.discountRatePercentage.max,
                  label: 'Increase',
                  align: 'right',
                },
              ]}
            />
          </div>
          <LabelledValue label="Current discount rate">
            {companyFinancialData.discountRatePercentage.toFixed(2)}%
          </LabelledValue>
        </div>
      </div>

      {/* Calculation section */}
      <div className="grid gap-x2">
        <div className="grid grid-cols-[auto_min-content] items-center px-x2">
          <div className="text-xs font-strong text-solid">Calculation</div>
          <NarrativeValuatorTooltip>
            {tooltipContent.calculationBreakdown}
          </NarrativeValuatorTooltip>
        </div>

        {/* First tile */}
        {/* Earnings 2029 X PE Ratio 2029 = Market Cap 2029 */}
        <div className="grid grid-cols-[auto_1fr_auto_1fr_auto] gap-x1 bg-p-grey-30 p-x2 text-tiny">
          <div>
            <div className="text-chart-02">{futureEarnings}</div>
            <div className="text-solid">Earnings {futureYear}</div>
          </div>

          <OperatorSymbol>x</OperatorSymbol>

          <div>
            <div className="text-chart-03">
              {priceEarningsMultiple.toFixed(2)}x
            </div>
            <div className="text-solid">PE Ratio {futureYear}</div>
          </div>

          <OperatorSymbol>=</OperatorSymbol>

          <div>
            <div className="text-chart-04">{futureMarketCap}</div>
            <div className="text-solid">Market Cap {futureYear}</div>
          </div>
        </div>

        {/* Second tile */}
        {/* Market Cap 2029 / Number of Shares Outstanding 2029 = Share Price 2029 */}
        <div className="grid grid-cols-[auto_1fr_auto_1fr_auto] gap-x1 bg-p-grey-30 p-x2 text-tiny">
          <div>
            <div className="text-chart-04">{futureMarketCap}</div>
            <div className="text-solid">Market Cap {futureYear}</div>
          </div>

          <OperatorSymbol>/</OperatorSymbol>

          <div>
            <div className="text-softer">{futureSharesOutstanding}</div>
            <div className="text-solid">No. shares {futureYear}</div>
          </div>

          <OperatorSymbol>=</OperatorSymbol>

          <div>
            <div className="text-chart-04">{sharePriceFutureYear}</div>
            <div className="text-solid">Share Price {futureYear}</div>
          </div>
        </div>

        {/* Third tile */}
        {/* 2029 Share Price (Discounted Back @ DR) = Fair Value Estimate 2024 */}
        <div className="grid grid-cols-[auto_1fr_auto] rounded-b-x1 bg-p-grey-30 p-x2 text-tiny">
          <div>
            <div>
              <span className="text-chart-04">{sharePriceFutureYear}</span>

              <span className="text-solid"> Share Price {futureYear}</span>
            </div>
            <div className="text-softer">
              Discounted to {mostRecentReportedYear} @{' '}
              {discountRatePercentage.toFixed(2)}% p.a.
            </div>
          </div>

          <OperatorSymbol>=</OperatorSymbol>

          <div>
            <div className="text-chart-01">{fairValueReportedPrice}</div>
            <div className="text-solid">Fair Value {currentYear}</div>
          </div>
        </div>

        {/* Fourth tile */}
        {/* Optional currency conversion */}
        {requiresCurrencyConversion && (
          <div className="grid grid-cols-[4fr_1fr_auto] rounded-b-x1 bg-p-grey-30 p-x2 text-tiny">
            <div>
              <div>
                <span className="text-chart-04">{fairValueReportedPrice}</span>

                <span className="text-solid"> Fair Value {currentYear}</span>
              </div>
              <div className="text-softer">
                Converted to {listingCurrencyISO} @ {exchangeRate} Exchange Rate
              </div>
            </div>

            <OperatorSymbol>=</OperatorSymbol>

            <div>
              <div className="text-chart-01">{fairValueListedPrice}</div>
              <div className="text-solid">Fair Value {currentYear}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
